/**
 * PermissionsEditor — modal admin pentru editarea permisiilor granulare
 * ale unui user (sales/partner). Adminii nu pot fi modificați.
 *
 * Props:
 *   open, onOpenChange — controlled dialog state
 *   user               — { id, email, role, permissions? }
 *   onSaved(updated)   — callback după save reuşit
 *
 * Foloseşte:
 *   GET  /api/admin/permissions/catalog → catalog + preset-uri
 *   PATCH /api/admin/users/{id}/permissions → salvare
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Shield, ShieldCheck, Save, Sparkles } from "lucide-react";

export default function PermissionsEditor({ open, onOpenChange, user, onSaved }) {
  const [catalog, setCatalog] = useState(null);   // {permissions: [...], presets: [...]}
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [presetKey, setPresetKey] = useState("");

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/permissions/catalog");
      setCatalog(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nu am putut încărca catalogul de permisii.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && !catalog) loadCatalog();
    if (open && user) {
      setSelected(new Set(Array.isArray(user.permissions) ? user.permissions : []));
      setPresetKey("");
    }
  }, [open, user, catalog, loadCatalog]);

  // Group permissions by category
  const grouped = useMemo(() => {
    if (!catalog?.permissions) return {};
    return catalog.permissions.reduce((acc, p) => {
      acc[p.category] = acc[p.category] || [];
      acc[p.category].push(p);
      return acc;
    }, {});
  }, [catalog]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setPresetKey(""); // reset preset indicator when user manually changes
  };

  const toggleCategory = (category, allSelected) => {
    const keys = (grouped[category] || []).map((p) => p.key);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
    setPresetKey("");
  };

  const applyPreset = (key) => {
    if (!catalog) return;
    const preset = catalog.presets.find((p) => p.key === key);
    if (!preset) return;
    setSelected(new Set(preset.permissions));
    setPresetKey(key);
    toast.info(`Preset "${preset.label}" aplicat — verifică și salvează.`);
  };

  const clearAll = () => { setSelected(new Set()); setPresetKey(""); };

  const doSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const perms = Array.from(selected).sort();
      const { data } = await api.patch(`/admin/users/${user.id}/permissions`, {
        permissions: perms,
      });
      trackEvent("admin.perm.change", {
        target_role: user.role,
        perms_count: perms.length,
      });
      toast.success(`Permisiile pentru ${user.email} au fost actualizate (${perms.length} active).`);
      onSaved?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Salvarea a eșuat.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const total = catalog?.permissions?.length || 0;
  const activeCount = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-zinc-950 border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="permissions-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-400">
            <Shield size={18} />
            Permisii pentru {user.email}
            <span className="ml-auto text-xs font-normal text-zinc-500">
              {activeCount} / {total} active
            </span>
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Rol: <span className="uppercase tracking-wide text-orange-300">{user.role}</span>
            {" · "}
            Adminul poate schimba în orice moment. Modificările intră în vigoare la următorul login.
          </DialogDescription>
        </DialogHeader>

        {loading || !catalog ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-orange-400" />
          </div>
        ) : (
          <>
            {/* PRESET ROW */}
            <div className="flex items-center gap-3 border-y border-white/5 py-3 px-1">
              <Sparkles size={14} className="text-orange-400" />
              <span className="text-xs text-zinc-400">Aplică preset:</span>
              <Select
                value={presetKey}
                onValueChange={(v) => v && applyPreset(v)}
              >
                <SelectTrigger
                  className="h-8 w-64 bg-zinc-900 border-white/10 text-white text-xs"
                  data-testid="permissions-preset-trigger"
                >
                  <SelectValue placeholder="Alege un preset rapid..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {catalog.presets.map((p) => (
                    <SelectItem
                      key={p.key}
                      value={p.key}
                      className="text-xs"
                      data-testid={`preset-${p.key}`}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="ml-auto text-xs text-zinc-400 hover:text-white"
                data-testid="permissions-clear-btn"
              >
                Deselectează toate
              </Button>
            </div>

            {/* GROUPED PERMISSIONS LIST */}
            <div className="overflow-y-auto flex-1 space-y-4 pr-2 py-3">
              {Object.entries(grouped).map(([category, perms]) => {
                const allInCat = perms.every((p) => selected.has(p.key));
                return (
                  <div key={category} className="border border-white/5 rounded-lg bg-zinc-900/40">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                      <div className="text-xs uppercase tracking-[0.2em] text-orange-400 font-semibold">
                        {category}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleCategory(category, allInCat)}
                        className="text-[10px] text-zinc-500 hover:text-orange-400 uppercase tracking-wider"
                        data-testid={`toggle-category-${category}`}
                      >
                        {allInCat ? "Deselectează toate" : "Selectează toate"}
                      </button>
                    </div>
                    <div className="p-3 space-y-2">
                      {perms.map((p) => {
                        const isOn = selected.has(p.key);
                        return (
                          <label
                            key={p.key}
                            className={`flex items-start gap-3 cursor-pointer p-2 rounded transition ${
                              isOn ? "bg-orange-500/5 border border-orange-500/20" : "hover:bg-white/5"
                            }`}
                            data-testid={`perm-row-${p.key}`}
                          >
                            <Checkbox
                              checked={isOn}
                              onCheckedChange={() => toggle(p.key)}
                              className="mt-0.5"
                              data-testid={`perm-checkbox-${p.key}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2">
                                <span className="text-sm text-white font-medium">{p.label}</span>
                                <code className="text-[10px] text-zinc-500 bg-zinc-950 px-1.5 py-0.5 rounded">
                                  {p.key}
                                </code>
                              </div>
                              <p className="text-xs text-zinc-400 mt-0.5">{p.desc}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="border-t border-white/5 pt-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={saving}
            className="text-zinc-400"
            data-testid="permissions-cancel-btn"
          >
            Anulează
          </Button>
          <Button
            onClick={doSave}
            disabled={saving || loading}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="permissions-save-btn"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin mr-1.5" />
            ) : (
              <Save size={14} className="mr-1.5" />
            )}
            Salvează ({activeCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
